// 素材庫の上部: 「検知した投稿」（第3弾）。写真に @stoq_kitchen をタグ付けした投稿を自動で拾った結果のうち、
// まだ案件に紐づいていないもの（クリエイター登録済み＝creator_no_deal／未登録アカウント＝unknown）を一覧にする。
// 操作: 案件に紐づける（そのクリエイターの段階4〜7の案件から選ぶ）／クリエイターとして追加／無視（あとで戻せる）。
import { useMemo, useState } from "react";
import { putGfIgDetectedMedia } from "../../../../api/giftingClient";
import { STAGE_NAMES, dealsForMedia, fmtMD, igDetectCounts } from "../../../../lib/giftingRules";
import type { GfIgDetectedMedia, GfIgMediaFilter, GfTabProps } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { CreatorFormModal } from "../../modals/CreatorFormModal";
import { useGfIgDetectedMedia } from "../../useGfIgDetect";
import { GfButton, GfCard, GfChip, GfEmpty, GfErrorBanner, GfSkeleton, GfTag } from "../../ui/GfUi";

const FILTERS: Array<{ value: Extract<GfIgMediaFilter, "unlinked" | "ignored">; label: string }> = [
  { value: "unlinked", label: "未紐づけ" },
  { value: "ignored", label: "無視した投稿" },
];

const KIND_LABEL: Record<string, string> = {
  REELS: "リール",
  STORY: "ストーリー",
  CAROUSEL_ALBUM: "カルーセル",
  VIDEO: "動画",
  IMAGE: "写真",
};

function kindOf(m: GfIgDetectedMedia): string {
  return KIND_LABEL[m.media_product_type ?? ""] ?? KIND_LABEL[m.media_type ?? ""] ?? "投稿";
}

export function DetectedPosts(props: GfTabProps) {
  const { data, refresh, toast } = props;
  const [filter, setFilter] = useState<"unlinked" | "ignored">("unlinked");
  const [open, setOpen] = useState<boolean | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // 未紐づけの件数バッジは、表示中のフィルタに関係なく常に未紐づけ一覧から数える
  const unlinked = useGfIgDetectedMedia("unlinked", reloadKey);
  const ignored = useGfIgDetectedMedia("ignored", reloadKey);
  const [picking, setPicking] = useState<string | null>(null);
  const [dealId, setDealId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState<GfIgDetectedMedia | null>(null);

  const counts = igDetectCounts(unlinked.media.status === "ok" ? unlinked.media.data : []);
  // 既定の開閉: 確認が必要な（クリエイター登録済みの）投稿があれば開いておく
  const isOpen = open ?? counts.review > 0;
  const list = filter === "unlinked" ? unlinked.media : ignored.media;

  const campaignName = useMemo(() => new Map(data.board.campaigns.map((c) => [c.id, c.name])), [data.board.campaigns]);

  const reload = async () => {
    setReloadKey((k) => k + 1);
    await refresh();
  };

  const act = async (m: GfIgDetectedMedia, fn: () => Promise<unknown>, okMsg: string): Promise<boolean> => {
    setBusy(m.media_id);
    try {
      await fn();
      toast(okMsg);
      await reload();
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const startPick = (m: GfIgDetectedMedia, candidates: ReturnType<typeof dealsForMedia>) => {
    if (picking === m.media_id) return setPicking(null);
    setPicking(m.media_id);
    setDealId(candidates[0]?.id ?? "");
  };

  const link = async (m: GfIgDetectedMedia) => {
    if (!dealId) return;
    const entry = data.board.entries.find((e) => e.id === dealId);
    const ok = await act(
      m,
      () => putGfIgDetectedMedia(m.media_id, { deal_id: dealId }),
      entry?.stage === 5 ? "案件に紐づけました（投稿確認の段階へ進みました）" : "案件に紐づけました"
    );
    if (ok) setPicking(null);
  };

  const body = () => {
    if (list.status === "loading") return <GfSkeleton height={64} />;
    if (list.status === "error") return <GfErrorBanner message={`検知した投稿を取得できませんでした（${list.message}）`} onRetry={() => void reload()} />;
    if (list.data.length === 0) {
      return <GfEmpty>{filter === "unlinked" ? "未紐づけの検知はありません" : "無視した投稿はありません"}</GfEmpty>;
    }
    return (
      <ul className="gf-detect-list">
        {list.data.map((m) => {
          const registered = !!m.influencer_id;
          const candidates = registered ? dealsForMedia(data.board.entries, m.influencer_id) : [];
          const working = busy === m.media_id;
          return (
            <li className="gf-detect-row" key={m.media_id}>
              <a className={`gf-detect-thumb${m.thumbnail_url ? "" : " gf-placeholder"}`} href={m.permalink} target="_blank" rel="noopener noreferrer" aria-label="投稿を開く">
                {m.thumbnail_url && <img src={m.thumbnail_url} alt="" loading="lazy" />}
              </a>
              <div className="gf-detect-main">
                <div className="gf-detect-top">
                  <span className="gf-name-strong">@{m.username}</span>
                  {registered ? <GfTag tone="info">クリエイター登録済み{m.influencer_name ? `（${m.influencer_name}）` : ""}</GfTag> : <GfTag tone="muted">未登録アカウント</GfTag>}
                  <span className="gf-mono gf-note">
                    {kindOf(m)}・{fmtMD(m.taken_at)}
                  </span>
                  <a className="gf-link-btn" href={m.permalink} target="_blank" rel="noopener noreferrer">
                    投稿を開く
                  </a>
                </div>
                {m.caption && <div className="gf-detect-caption">{m.caption}</div>}
                {filter === "unlinked" && (
                  <>
                    <div className="gf-detect-actions">
                      {registered ? (
                        <GfButton size="sm" variant="primary" disabled={working} onClick={() => startPick(m, candidates)}>
                          案件に紐づける
                        </GfButton>
                      ) : (
                        <GfButton size="sm" variant="primary" disabled={working} onClick={() => setAdding(m)}>
                          クリエイターとして追加
                        </GfButton>
                      )}
                      <GfButton size="sm" disabled={working} onClick={() => void act(m, () => putGfIgDetectedMedia(m.media_id, { status: "ignored" }), "この投稿を無視しました")}>
                        無視
                      </GfButton>
                    </div>
                    {registered && picking === m.media_id && (
                      <div className="gf-boxed gf-detect-pick">
                        {candidates.length === 0 ? (
                          <span className="gf-note">この人の「住所回収」〜「2次利用許諾」の案件がありません。先に案件へ追加し、進めてください。</span>
                        ) : (
                          <>
                            <select className="gf-select" aria-label="紐づける案件" value={dealId} onChange={(e) => setDealId(e.target.value)}>
                              {candidates.map((c) => {
                                const e = data.board.entries.find((x) => x.id === c.id);
                                return (
                                  <option key={c.id} value={c.id}>
                                    {(e?.campaign_id && campaignName.get(e.campaign_id)) || e?.product_name || c.id}・{STAGE_NAMES[c.stage]}
                                  </option>
                                );
                              })}
                            </select>
                            <span className="gf-note gf-lh">「投稿待ち」の案件に紐づけると、投稿を記録して「投稿確認」の段階へ進めます。</span>
                            <div className="gf-actions">
                              <GfButton size="sm" variant="primary" disabled={working || !dealId} onClick={() => void link(m)}>
                                {working ? "紐づけ中…" : "紐づける"}
                              </GfButton>
                              <GfButton size="sm" onClick={() => setPicking(null)}>
                                やめる
                              </GfButton>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
                {filter === "ignored" && (
                  <div className="gf-detect-actions">
                    <GfButton size="sm" disabled={working} onClick={() => void act(m, () => putGfIgDetectedMedia(m.media_id, { status: "unlinked" }), "未紐づけに戻しました")}>
                      未紐づけに戻す
                    </GfButton>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <GfCard className="gf-detect-card" flush>
      <button type="button" className="gf-detect-head" aria-expanded={isOpen} onClick={() => setOpen(!isOpen)}>
        <span className="gf-h2">検知した投稿</span>
        {unlinked.media.status === "ok" && counts.total > 0 && <span className="gf-detect-badge">{counts.total}</span>}
        <span className="gf-note gf-detect-sub">
          {unlinked.media.status === "ok"
            ? counts.total === 0
              ? "未紐づけはありません"
              : `要確認 ${counts.review}件${counts.sales > 0 ? `・未登録アカウント ${counts.sales}件` : ""}`
            : unlinked.media.status === "error"
              ? "取得できませんでした"
              : ""}
        </span>
        <span className="gf-detect-caret" aria-hidden="true">
          {isOpen ? "▲" : "▼"}
        </span>
      </button>
      {isOpen && (
        <div className="gf-detect-body">
          <div className="gf-note gf-lh">
            写真に @stoq_kitchen をタグ付けした投稿を、毎時自動で拾っています（ストーリー・キャプションだけのメンションは対象外です。従来どおり本人報告か手動で記録してください）。
          </div>
          <div className="gf-chips gf-chips-sm">
            {FILTERS.map((f) => (
              <GfChip
                key={f.value}
                dark
                active={filter === f.value}
                sub={f.value === "unlinked" ? (counts.total > 0 ? counts.total : undefined) : ignored.media.status === "ok" && ignored.media.data.length > 0 ? ignored.media.data.length : undefined}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </GfChip>
            ))}
          </div>
          {body()}
        </div>
      )}
      <CreatorFormModal
        {...props}
        open={!!adding}
        initialHandle={adding?.username}
        onClose={() => setAdding(null)}
        onSaved={() => {
          const m = adding;
          setAdding(null);
          // 登録後は「クリエイター登録済み」として判定し直してもらう（status=unlinked は登録の有無で振り分け直す）
          if (m) void putGfIgDetectedMedia(m.media_id, { status: "unlinked" }).catch(() => undefined).then(() => reload());
          else void reload();
        }}
      />
    </GfCard>
  );
}
